import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type MixLayout = 'light-only' | 'dark-only';

interface ThemeContextValue {
  layout: string;
  sidebarIconType: string;
  toggleIcon: boolean;
  mixLayout: MixLayout;
  toggleSidebar: (closed?: boolean) => void;
  setToggleIcon: (closed: boolean) => void;
  setMixLayout: (mix: MixLayout) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const LAYOUT = 'compact-wrapper';
const SIDEBAR_ICON = 'stroke-svg';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [toggleIcon, setToggleIcon] = useState(false);
  const [mixLayout, setMixLayoutState] = useState<MixLayout>(() => {
    const saved = localStorage.getItem('mix_background_layout');
    return saved === 'dark-only' ? 'dark-only' : 'light-only';
  });

  useEffect(() => {
    document.body.classList.remove('light-only', 'dark-only');
    document.body.classList.add(mixLayout);
    if (!document.body.classList.contains('ltr') && !document.body.classList.contains('rtl')) {
      document.body.classList.add('ltr');
    }
  }, [mixLayout]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth <= 991) setToggleIcon(true);
      else setToggleIcon(false);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleSidebar = useCallback((closed?: boolean) => {
    setToggleIcon((prev) => (typeof closed === 'boolean' ? closed : !prev));
  }, []);

  const setMixLayout = useCallback((mix: MixLayout) => {
    localStorage.setItem('mix_background_layout', mix);
    setMixLayoutState(mix);
  }, []);

  const value = useMemo(
    () => ({
      layout: LAYOUT,
      sidebarIconType: SIDEBAR_ICON,
      toggleIcon,
      mixLayout,
      toggleSidebar,
      setToggleIcon,
      setMixLayout,
    }),
    [toggleIcon, mixLayout, toggleSidebar, setMixLayout],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
